import * as Sentry from '@sentry/react';
import { trackDesignSystemAction } from 'analytics';
import ColorButton from 'components/Widget/Button/ColorButton';
import Icon, { IconType } from 'components/Icon';
import { Tooltip } from 'components/Widget/Tooltip';
import { DEFAULT_DS_TITLE } from 'dragonmouse-shared/constants';
import useSidebarDropdown from 'hooks/useSidebarDropdown';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import forge from 'apis/forge';
import { setPlanPricingModalVisible, setSidebarVisible } from 'redux/mode/actions';
import { RootReducer } from 'redux/reducers';
import { selectIsMobile } from 'redux/mode/reducers';
import { selectIsGuest } from 'redux/db/reducers';
import * as routes from 'routes';
import { useTheme } from 'styled-components';
import { DeferredTab, openDeferredInternalTab, openInternalTab } from 'utils';
import * as S from './styles';

// The icon names the noun, the label names the variant: a blank project is
// still a project, so it shares the project's mark rather than getting its own.
// STACK is the design system's mark everywhere else in the app (DsPicker).
const PROJECT_ICON = IconType.GRID;
const DS_ICON = IconType.STACK;

// Both lines wrap to two at the popover's width, so the pair reads as equals
// and together they outweigh the utility rows below.
const PROJECT_SUBCOPY = 'Describe your idea and Moonchild designs it, screen by screen';
const DS_SUBCOPY = 'Colors, components, patterns and standards — reusable across your projects';

const NEW_TAB_TOOLTIP_ID = 'create-menu-new-tab';

const NewButton = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const theme = useTheme();
	const { isMobile, isGuest } = useSelector(
		(r: RootReducer) => ({
			isMobile: selectIsMobile(r.mode),
			isGuest: selectIsGuest(r.db),
		}),
		shallowEqual
	);
	const [isCreatingProject, setIsCreatingProject] = useState(false);
	const [isCreatingDs, setIsCreatingDs] = useState(false);
	const { isOpen, toggle, close, containerRef } = useSidebarDropdown();

	//----------------------------------------------------------------------
	// HANDLERS
	//----------------------------------------------------------------------

	// Every create path is gated the same way: guests are pushed to pricing, and
	// on mobile the sidebar gets out of the way once the destination is chosen.
	const guarded = (action: () => void) => () => {
		close();
		if (isGuest) {
			dispatch(setPlanPricingModalVisible(true));
			return;
		}
		action();
		if (isMobile) dispatch(setSidebarVisible(false));
	};

	const onNewProject = guarded(() => navigate('/'));
	const onNewDesignSystem = guarded(() => {
		trackDesignSystemAction('launcher_new_ds');
		navigate(routes.createDesignSystem());
	});
	const onProjectCreatorInNewTab = guarded(() => openInternalTab(routes.home()));
	const onDsCreatorInNewTab = guarded(() => {
		trackDesignSystemAction('launcher_new_ds', { newTab: true });
		openInternalTab(routes.createDesignSystem());
	});

	// The blank rows resolve to a created entity, so their new-tab variant must
	// open the tab before the request (popup blockers) and close it on failure
	const createBlankProject = async (tab?: DeferredTab) => {
		if (isCreatingProject) {
			tab?.close();
			return;
		}
		setIsCreatingProject(true);
		try {
			const res = await forge.createScene.withToast()({ title: 'My Project' });
			const url = routes.scene(res.sceneId, 'My Project');
			if (tab) {
				tab.navigate(url);
			} else {
				navigate(url);
			}
		} catch (e) {
			// User-facing error handling is done by withToast()
			Sentry.captureException(e);
			tab?.close();
		} finally {
			setIsCreatingProject(false);
		}
	};

	// Lands straight in a default-named design system; renaming happens in the
	// editor, same as a blank project
	const createBlankDesignSystem = async (tab?: DeferredTab) => {
		if (isCreatingDs) {
			tab?.close();
			return;
		}
		setIsCreatingDs(true);
		try {
			trackDesignSystemAction('created_blank_ds', { source: 'launcher' });
			const res = await forge.createDesignSystem.withToast()({ title: DEFAULT_DS_TITLE });
			const url = routes.designSystem(res.designSystem.id, res.designSystem.title);
			if (tab) {
				tab.navigate(url);
			} else {
				navigate(url);
			}
		} catch (e) {
			// User-facing error handling is done by withToast()
			Sentry.captureException(e);
			tab?.close();
		} finally {
			setIsCreatingDs(false);
		}
	};

	//----------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------

	if (isMobile) {
		return (
			<S.Base>
				<ColorButton label="New Project" icon={IconType.ADD} onClick={onNewProject} fullWidth data-testid="new-project-button" />
			</S.Base>
		);
	}

	const primaries = [
		{
			icon: PROJECT_ICON,
			title: 'Project',
			hint: PROJECT_SUBCOPY,
			onClick: onNewProject,
			onNewTab: onProjectCreatorInNewTab,
			newTabLabel: 'Open project creator in new tab',
			testId: 'new-project-button',
		},
		{
			icon: DS_ICON,
			title: 'Design System',
			hint: DS_SUBCOPY,
			onClick: onNewDesignSystem,
			onNewTab: onDsCreatorInNewTab,
			newTabLabel: 'Open design system creator in new tab',
			testId: 'new-design-system-button',
		},
	];

	const secondaries = [
		{
			label: isCreatingProject ? 'Creating…' : 'Blank project',
			icon: PROJECT_ICON,
			onClick: guarded(() => createBlankProject()),
			onNewTab: guarded(() => createBlankProject(openDeferredInternalTab())),
			newTabLabel: 'Create blank project in new tab',
			disabled: isCreatingProject,
			testId: 'blank-project-button',
		},
		{
			label: isCreatingDs ? 'Creating…' : 'Blank design system',
			icon: DS_ICON,
			onClick: guarded(() => createBlankDesignSystem()),
			onNewTab: guarded(() => createBlankDesignSystem(openDeferredInternalTab())),
			newTabLabel: 'Create blank design system in new tab',
			disabled: isCreatingDs,
			testId: 'blank-design-system-button',
		},
	];

	const renderNewTabButton = (onNewTab: () => void, label: string) => (
		<S.NewTabButton
			type="button"
			aria-label={label}
			data-tooltip-id={NEW_TAB_TOOLTIP_ID}
			data-tooltip-content={label}
			onClick={onNewTab}
		>
			<Icon type={IconType.OPEN_EXTERNAL} width={13} height={13} color={theme.gray[600]} />
		</S.NewTabButton>
	);

	return (
		<S.Base ref={containerRef}>
			<ColorButton
				label="Create"
				icon={IconType.ADD}
				onClick={toggle}
				fullWidth
				data-testid="sidebar-create-button"
			/>
			{isOpen && (
				<S.Popover>
					{primaries.map((item) => (
						<S.RichRow key={item.title}>
							<S.RichMain
								type="button"
								onClick={(e) => (e.metaKey || e.ctrlKey ? item.onNewTab() : item.onClick())}
								data-testid={item.testId}
							>
								<S.IconCol>
									<Icon type={item.icon} width={15} height={15} color={theme.gray[1000]} />
								</S.IconCol>
								<S.RichText>
									<S.RichTitle>{item.title}</S.RichTitle>
									<S.RichSub>{item.hint}</S.RichSub>
								</S.RichText>
							</S.RichMain>
							{renderNewTabButton(item.onNewTab, item.newTabLabel)}
						</S.RichRow>
					))}
					<S.Divider />
					{secondaries.map((item) => (
						<S.PlainRow key={item.newTabLabel}>
							<S.PlainMain
								type="button"
								onClick={(e) => (e.metaKey || e.ctrlKey ? item.onNewTab() : item.onClick())}
								disabled={item.disabled}
								data-testid={item.testId}
							>
								<S.IconCol>
									<Icon type={item.icon} width={14} height={14} color={theme.gray[600]} />
								</S.IconCol>
								{item.label}
							</S.PlainMain>
							{!item.disabled && renderNewTabButton(item.onNewTab, item.newTabLabel)}
						</S.PlainRow>
					))}
					<Tooltip id={NEW_TAB_TOOLTIP_ID} placement="top" />
				</S.Popover>
			)}
		</S.Base>
	);
};

export default NewButton;
